import { useEffect, useRef, useState } from "react";
import { X, ScanLine } from "lucide-react";

export const BarcodeScanner = ({ onDetected, onClose }) => {
  const videoRef = useRef(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let stream, interval, stopped = false;
    const start = async () => {
      if (!("BarcodeDetector" in window)) {
        setError("Camera scanning isn't supported in this browser yet — type the barcode instead.");
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        if (stopped) { stream.getTracks().forEach((t) => t.stop()); return; }
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        const detector = new window.BarcodeDetector({
          formats: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128"],
        });
        interval = setInterval(async () => {
          if (!videoRef.current || videoRef.current.readyState < 2) return;
          try {
            const codes = await detector.detect(videoRef.current);
            if (codes.length > 0) {
              clearInterval(interval);
              stream.getTracks().forEach((t) => t.stop());
              onDetected(codes[0].rawValue);
            }
          } catch { /* keep scanning */ }
        }, 400);
      } catch {
        setError("Couldn't access the camera — check permissions, or type the barcode instead.");
      }
    };
    start();
    return () => {
      stopped = true;
      if (interval) clearInterval(interval);
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, [onDetected]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(10,9,8,0.85)" }}
      onClick={onClose} data-testid="barcode-scanner-modal">
      <div className="card-luxe max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="overline flex items-center gap-2"><ScanLine size={13} style={{ color: "var(--accent)" }} /> Scan a barcode</div>
          <button className="btn-ghost" onClick={onClose} data-testid="close-scanner"><X size={15} /></button>
        </div>
        {error ? (
          <p className="text-sm text-secondary-luxe mt-5" data-testid="scanner-error">{error}</p>
        ) : (
          <div className="mt-5 relative">
            <video ref={videoRef} muted playsInline className="w-full" style={{ background: "#000", aspectRatio: "4/3", objectFit: "cover" }} data-testid="scanner-video" />
            <div className="absolute inset-x-8 top-1/2 h-0.5 -translate-y-1/2" style={{ background: "var(--accent)", opacity: 0.7 }} />
            <p className="text-xs text-tertiary-luxe mt-3 text-center">Hold the barcode steady inside the frame.</p>
          </div>
        )}
      </div>
    </div>
  );
};
